import React, { useEffect, useState } from 'react';
import { usePageMeta } from './seo';
import './Faizi.css';

import StarfieldBackground from './components/reactbits/StarfieldBackground';
import LiquidNav from './components/reactbits/LiquidNav';
import EnterGate from './components/reactbits/EnterGate';
import BlurText from './components/reactbits/BlurText';
import ClickSpark from './components/reactbits/ClickSpark';
import FadeContent from './components/reactbits/FadeContent';

import CountUp from './components/reactbits/CountUp';
import SpotlightCard from './components/reactbits/SpotlightCard';
import FancyActionButton from './components/reactbits/FancyActionButton';
import ScrollVelocity from './components/reactbits/ScrollVelocity';
import SocialButtons from './components/reactbits/SocialButtons';
import ProfileFlipCard from './components/reactbits/ProfileFlipCard';

import profileImage from './pfp.png';
import authidexLogo from './mainlogo.png';
import xcrowhubLogo from './xcrowhub.png';

const NAV_ITEMS = [
  { label: 'Home', href: '#hero' },
  { label: 'About', href: '#about' },
  { label: 'Projects', href: '#authidex' },
  { label: 'Contact', href: '#contact' },
];

// All project sections roll up to the single "Projects" nav item.
const PROJECT_HREFS = ['#authidex', '#xcrowhub', '#sphericalguild'];

const SOCIAL_LINKS = [
  { label: 'Telegram', handle: '@faiziweb3', href: 'https://t.me/faiziweb3', icon: 'telegram' },
  { label: 'X', handle: '@faizionweb3', href: 'https://x.com/faizionweb3', icon: 'x' },
  { label: 'GitHub', handle: 'dropxpert', href: 'https://github.com/dropxpert', icon: 'github' },
  { label: 'LinkedIn', handle: 'faizidx', href: 'https://www.linkedin.com/in/faizidx/', icon: 'linkedin' },
];

const STATS = [
  { value: 1000, prefix: '', suffix: '+', separator: ',', label: 'Users scaled on Authidex' },
  { value: 2, prefix: '', suffix: ' wks', separator: '', label: 'From zero to 1,000 users' },
  { value: 3, prefix: '', suffix: '', separator: '', label: 'Products founded & shipped' },
  { value: 100, prefix: '', suffix: '%', separator: '', label: 'Solo-built, no team' },
];

const SKILLS_ROW_ONE = 'React  ·  Telegram Bots  ·  Node.js  ·  Web3  ·  Growth  ·  Mini Apps  ·';
const SKILLS_ROW_TWO = 'Frontend  ·  Backend  ·  Escrow Design  ·  Automation  ·  Community  ·  Content  ·';

const PROJECT_SECTIONS = [
  {
    id: 'authidex',
    name: 'Authidex',
    url: 'authidex.com',
    href: 'https://authidex.com',
    role: 'Founder',
    logo: authidexLogo,
    tagline: 'Screen X creators before campaign spend',
    description:
      'Authidex helps Web3 brands vet X (Twitter) creators before allocating campaign budget. Instead of paying for reach blindly, teams can screen creator credibility, audience quality, and campaign fit upfront, then run product-action campaigns where creators drive real usage, not just impressions.',
    features: [
      'Creator screening & credibility checks before spend',
      'Brand campaigns tied to real product actions',
      'Built for Web3 teams running X-native growth',
      '0 → 1,000 users in 2 weeks, built entirely solo',
    ],
    contribution:
      'Founded and built everything alone: product, frontend, backend, social channels, content strategy, and growth. No co-founder, no team.',
  },
  {
    id: 'xcrowhub',
    name: 'Xcrowhub',
    url: 'xcrowhub.com',
    href: 'https://xcrowhub.com',
    role: 'Founder',
    logo: xcrowhubLogo,
    tagline: 'Protected deals for crypto P2P',
    description:
      'Xcrowhub is a crypto peer-to-peer deal platform with built-in escrow protection. When two parties trade crypto directly, funds stay locked until both sides confirm the deal is complete, reducing scam risk and making informal P2P trades safer.',
    features: [
      'Escrow-protected crypto P2P transactions',
      'Funds held until deal conditions are met',
      'Designed for safer direct crypto trades',
      'End-to-end product vision and execution',
    ],
    contribution:
      'Founded Xcrowhub and own the full product direction, from UX to trust mechanics that make P2P crypto deals actually workable.',
  },
  {
    id: 'sphericalguild',
    name: 'Spherical Guild',
    url: 'sphericalguild.space',
    href: 'https://sphericalguild.space',
    role: 'Head Developer',
    tagline: 'Creator infrastructure for coordinated Web3 growth',
    description:
      'Spherical Guild is a creator-growth platform where Web3 projects launch structured campaigns and creators earn through measurable performance. Projects set objectives and reward pools; creators submit work via the Telegram Mini App; submissions are ranked and top performers get paid.',
    features: [
      'Campaign launches with defined objectives & reward pools',
      'Creator submissions through Telegram Mini App',
      'Performance-based ranking and reward distribution',
      'Spherical Opportunity Framework (SOF) for fair creator access',
    ],
    contribution:
      'Head Dev, leading frontend architecture, Telegram Mini App integration, production builds, and shipping the platform experience creators and projects use daily.',
  },
];

const Home = () => {
  const [activeSection, setActiveSection] = useState('#hero');
  const [isMobile, setIsMobile] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [entered, setEntered] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.sessionStorage?.getItem('faizi_entered') === '1';
  });


  // Lock page scroll while the enter gate is up.
  useEffect(() => {
    if (entered) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [entered]);

  const handleEnter = () => {
    window.sessionStorage?.setItem('faizi_entered', '1');
    setEntered(true);
  };

  usePageMeta({
    title: 'Faizi | Founder · Developer · Web3 Builder',
    description:
      'Web3 founder & full-stack dev specializing in Telegram bots. Founder of Authidex & Xcrowhub, Head Dev at Spherical Guild. Scaled Authidex 0→1,000 users in 2 weeks, solo.',
    canonical: '/',
    structuredData: {
      '@type': 'Person',
      name: 'Faizi',
      jobTitle: 'Founder, Developer, Telegram Bot Specialist',
      sameAs: [
        'https://t.me/faiziweb3',
        'https://x.com/faizionweb3',
        'https://github.com/dropxpert',
        'https://www.linkedin.com/in/faizidx/',
        'https://authidex.com',
        'https://xcrowhub.com',
        'https://sphericalguild.space',
      ],
    },
  });

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 768px)');
    const updateMobile = () => setIsMobile(mobileQuery.matches);
    updateMobile();
    mobileQuery.addEventListener('change', updateMobile);
    return () => mobileQuery.removeEventListener('change', updateMobile);
  }, []);



  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        const doc = document.documentElement;
        const max = doc.scrollHeight - doc.clientHeight;
        setScrollProgress(max > 0 ? Math.min(1, doc.scrollTop / max) : 0);
        frame = 0;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const sectionIds = ['#hero', '#about', ...PROJECT_SECTIONS.map((p) => `#${p.id}`), '#contact'];
    const sections = sectionIds.map((id) => document.querySelector(id)).filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(`#${entry.target.id}`);
          }
        });
      },
      { rootMargin: '-35% 0px -50% 0px', threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <ClickSpark
      sparkColor="rgba(148, 163, 184, 0.9)"
      sparkSize={8}
      sparkRadius={18}
      sparkCount={10}
      duration={380}
    >
      {!entered && <EnterGate onEnter={handleEnter} />}

      <div className="faizi-site">
        <div className="faizi-scroll-progress" aria-hidden="true">
          <span style={{ transform: `scaleX(${scrollProgress})` }} />
        </div>

        <div className="faizi-bg" aria-hidden="true">
          <StarfieldBackground />
        </div>

        <header className="faizi-nav-wrap">
          <LiquidNav
            logo={profileImage}
            logoAlt="Faizi"
            items={NAV_ITEMS}
            activeHref={PROJECT_HREFS.includes(activeSection) ? '#authidex' : activeSection}
          />
        </header>

        <main className="faizi-main">
          <section id="hero" className="faizi-section faizi-section--hero">
            <div className="faizi-hero">
              <div className="faizi-hero__copy">
                <h1 className="faizi-hero__name">
                  <BlurText text="Faizi" delay={70} animateBy="letters" direction="top" className="blur-text" />
                </h1>

                <h2 className="faizi-hero__subtitle">
                  <BlurText
                    text="Founder, Developer & Growth Lead"
                    delay={40}
                    animateBy="words"
                    direction="bottom"
                    threshold={0.2}
                  />
                </h2>

                <p className="faizi-hero__tagline">
                  I build and ship Web3 products end to end: frontend, backend, Telegram bots, growth, and content.
                  Scaled <strong>Authidex from 0 to 1,000 users in 2 weeks</strong>, solo.
                </p>

                <div className="faizi-hero__actions">
                  <FancyActionButton
                    href="#authidex"
                    label1="View Projects"
                    label2="See All"
                    variant="primary"
                  />
                  <FancyActionButton
                    href="https://x.com/faizionweb3"
                    label1="@faizionweb3"
                    label2="Follow"
                    variant="ghost"
                    external
                    wide
                  />
                </div>

                <dl className="faizi-hero__stats">
                  <div className="faizi-hero__stat">
                    <dt>
                      <CountUp to={1000} separator="," className="faizi-hero__stat-value" />
                      <span className="faizi-hero__stat-value">+</span>
                    </dt>
                    <dd>users scaled</dd>
                  </div>
                  <div className="faizi-hero__stat">
                    <dt>
                      <CountUp to={3} className="faizi-hero__stat-value" />
                    </dt>
                    <dd>products shipped</dd>
                  </div>
                  <div className="faizi-hero__stat">
                    <dt>
                      <CountUp to={2} className="faizi-hero__stat-value" />
                      <span className="faizi-hero__stat-value">wk</span>
                    </dt>
                    <dd>to first 1k</dd>
                  </div>
                </dl>
              </div>

              <div className="faizi-hero__visual">
                <FadeContent blur duration={1000} delay={150}>
                  <ProfileFlipCard
                    image={profileImage}
                    imageAlt="Faizi"
                    backTitle="Let's build"
                    backHref="#contact"
                  />
                </FadeContent>
              </div>
            </div>
          </section>

          <section className="faizi-section faizi-stats">
            <div className="faizi-stats__grid">
              {STATS.map((stat, index) => (
                <FadeContent key={stat.label} delay={index * 90} className="faizi-stats__item">
                  <SpotlightCard
                    className="faizi-card faizi-stat"
                    spotlightColor="rgba(228, 228, 231, 0.12)"
                  >
                    <span className="faizi-stat__value">
                      {stat.prefix}
                      <CountUp to={stat.value} separator={stat.separator} duration={2.2} />
                      {stat.suffix}
                    </span>
                    <span className="faizi-stat__label">{stat.label}</span>
                  </SpotlightCard>
                </FadeContent>
              ))}
            </div>
          </section>

          <div className="faizi-marquee" aria-hidden="true">
            <ScrollVelocity
              texts={[SKILLS_ROW_ONE, SKILLS_ROW_TWO]}
              velocity={isMobile ? 24 : 36}
              numCopies={4}
              className="faizi-marquee__text"
            />
          </div>

          <section id="about" className="faizi-section">
            <FadeContent>
              <div className="faizi-section-head">
                <span className="faizi-section-label">About</span>
                <h2 className="faizi-section-title">
                  <BlurText text="Who I am" delay={50} animateBy="words" direction="top" />
                </h2>
                <p className="faizi-section-desc">
                  21, based in India. Web3 founder and full-stack developer who also runs growth, community, and content.
                  I specialize in Telegram bot development and shipping complete products without a team.
                </p>
              </div>

              <div className="faizi-about-grid">
                <SpotlightCard className="faizi-card faizi-about-card" spotlightColor="rgba(228, 228, 231, 0.1)">
                  <h3 className="faizi-about-card__title">Background</h3>
                  <p>
                    I started in Web3 as a growth manager, community manager, and moderator across multiple projects.
                    That gave me a clear view of what communities need, what products fail to deliver, and where
                    campaigns waste budget. Instead of waiting for the right team, I learned to build and ship myself.
                  </p>
                  <p>
                    Today I work across product, code, distribution, and operations. No co-founder, no agency, no
                    outsourced dev. One person owning the full loop from idea to live users.
                  </p>
                </SpotlightCard>

                <SpotlightCard className="faizi-card faizi-about-card" spotlightColor="rgba(228, 228, 231, 0.1)">
                  <h3 className="faizi-about-card__title">Telegram bot development</h3>
                  <p>
                    I specialize in <strong>Telegram bot development</strong>: custom bots, automation flows, Mini App
                    integrations, and Web3-native experiences inside Telegram. Spherical Guild&apos;s creator submission
                    flow runs through a Telegram Mini App, and I build the bot logic, user flows, and production
                    integrations that make that work at scale.
                  </p>
                  <div className="faizi-about-tags">
                    <span>Telegram Bots</span>
                    <span>Mini Apps</span>
                    <span>Automation</span>
                    <span>Web3 Integrations</span>
                  </div>
                </SpotlightCard>

                <SpotlightCard className="faizi-card faizi-about-card" spotlightColor="rgba(228, 228, 231, 0.1)">
                  <h3 className="faizi-about-card__title">Authidex</h3>
                  <p>
                    Founded <strong>Authidex</strong> to solve a real Web3 marketing problem: brands pay X creators
                    without knowing if the audience is real or if the campaign will drive product usage. I built the
                    full platform solo and scaled it from <strong>0 to 1,000 users in 2 weeks</strong>.
                  </p>
                  <p>
                    I handled the website, frontend, backend, social channels, content strategy, and growth. Every
                    layer of the product and every channel that brought users in.
                  </p>
                </SpotlightCard>

                <SpotlightCard className="faizi-card faizi-about-card" spotlightColor="rgba(228, 228, 231, 0.1)">
                  <h3 className="faizi-about-card__title">Xcrowhub</h3>
                  <p>
                    Founded <strong>Xcrowhub</strong> as a protected crypto P2P deal platform. Peer-to-peer crypto
                    trades are fast but risky; escrow gives both sides a reason to complete the deal honestly. I own
                    the product vision, UX, and trust mechanics from start to finish.
                  </p>
                </SpotlightCard>

                <SpotlightCard className="faizi-card faizi-about-card" spotlightColor="rgba(228, 228, 231, 0.1)">
                  <h3 className="faizi-about-card__title">Spherical Guild</h3>
                  <p>
                    Head Developer at <strong>Spherical Guild</strong>, a creator-growth platform where Web3 projects
                    launch campaigns and creators earn through ranked performance. I lead frontend architecture,
                    Telegram Mini App work, and production releases for the ecosystem projects and creators use daily.
                  </p>
                </SpotlightCard>

                <SpotlightCard className="faizi-card faizi-about-card faizi-about-card--highlight" spotlightColor="rgba(228, 228, 231, 0.12)">
                  <h3 className="faizi-about-card__title">How I work</h3>
                  <p>
                    Full-stack development, Telegram bots, growth strategy, social media, content, community ops, and
                    campaign execution. I have done all of it alone on Authidex and apply the same approach across
                    every product I touch.
                  </p>
                  <ul className="faizi-about-list">
                    <li>React frontends and production web apps</li>
                    <li>Backend APIs, databases, and deployment</li>
                    <li>Telegram bots and Mini App development</li>
                    <li>Creator screening and campaign growth (Authidex)</li>
                    <li>Escrow and P2P product design (Xcrowhub)</li>
                    <li>Creator platform engineering (Spherical Guild)</li>
                  </ul>
                </SpotlightCard>
              </div>
            </FadeContent>
          </section>

          {PROJECT_SECTIONS.map((project, index) => (
            <section
              key={project.id}
              id={project.id}
              className={`faizi-section faizi-project-section ${index % 2 === 1 ? 'faizi-project-section--alt' : ''}`}
            >
              <FadeContent>
                <SpotlightCard className="faizi-card faizi-project-block" spotlightColor="rgba(228, 228, 231, 0.1)">
                  <div className="faizi-project-block__header">
                    <div className="faizi-project-block__identity">
                      {project.logo ? (
                        <img className="faizi-project-block__logo" src={project.logo} alt={`${project.name} logo`} />
                      ) : (
                        <div className="faizi-project-block__logo faizi-project-block__logo--placeholder">
                          {project.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <span className="faizi-project-block__role">{project.role}</span>
                        <h2 className="faizi-project-block__name">
                          <BlurText text={project.name} delay={40} animateBy="letters" direction="top" />
                        </h2>
                        <p className="faizi-project-block__url">{project.url}</p>
                      </div>
                    </div>
                    <a
                      className="faizi-btn faizi-btn--ghost faizi-project-block__cta"
                      href={project.href}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Visit site →
                    </a>
                  </div>

                  <p className="faizi-project-block__tagline">{project.tagline}</p>
                  <p className="faizi-project-block__desc">{project.description}</p>

                  <ul className="faizi-project-block__features">
                    {project.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>

                  <p className="faizi-project-block__contribution">
                    <strong>My role:</strong> {project.contribution}
                  </p>
                </SpotlightCard>
              </FadeContent>
            </section>
          ))}

          <section id="contact" className="faizi-section faizi-contact">
            <FadeContent>
              <div className="faizi-section-head faizi-section-head--center">
                <span className="faizi-section-label">Contact</span>
                <h2 className="faizi-section-title">
                  <BlurText text="Get in touch" delay={50} animateBy="words" direction="top" />
                </h2>
                <p className="faizi-section-desc">
                  Open to collaborations and ambitious Web3 projects.
                </p>
              </div>

              <SocialButtons links={SOCIAL_LINKS} />
            </FadeContent>
          </section>

          <footer className="faizi-footer">
            <p>© {new Date().getFullYear()} Faizi · faizidx.in</p>
          </footer>
        </main>
      </div>
    </ClickSpark>
  );
};

export default Home;